import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, gtagEvent, vars } from '@agendor/ui';
import BusinessService, {
    BusinessDetails,
    ConnectorApprovalRateLimitedError,
    ConnectorCodeExpiredError,
    ConnectorTermsNotAcceptedError,
    WhatsAppAlreadyConnectedError,
    WhatsAppUnavailableError,
    hasPaidAccess,
} from '../business-service';
import { ConnectorTermsCheckbox } from '../sections/WhatsAppConnectorPanel';

const Code = styled.div`
    font-size: 1.6rem;
    font-weight: 700;
    letter-spacing: 0.15em;
    text-align: center;
    padding: 12px;
    background: ${vars.backgroundColorBright};
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    line-height: 1.5;
`;

const Caution = styled.span`
    font-size: 0.9rem;
    opacity: 0.8;
`;

const ReplaceWarning = styled.div`
    padding: 12px 16px;
    background-color: ${vars.warningTintSoft};
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    font-size: 0.9rem;
`;

const ErrorText = styled.span`
    color: ${vars.dangerColorDark};
    font-size: 0.9rem;
`;

type Blocker = 'worker' | 'unavailable' | 'cloud_api';
type Failure = 'expired' | 'unavailable' | 'cloud_api' | 'terms' | 'rate_limited' | 'unknown';

const blockerMessages: Record<Blocker, string> = {
    worker: 'Só o dono do estabelecimento pode conectar um computador. Saia e entre com a conta do dono; o código fica guardado.',
    unavailable: 'Seu plano atual não inclui o assistente no WhatsApp.',
    cloud_api:
        'Seu WhatsApp já está conectado pela conexão oficial. Desconecte-a no Assistente antes de usar o computador.',
};

const failureMessages: Record<Failure, string> = {
    expired:
        'Este código expirou ou já foi usado. Volte ao OrganizaBot Conector e clique em “Entrar com minha conta” para começar de novo.',
    unavailable: blockerMessages.unavailable,
    cloud_api: blockerMessages.cloud_api,
    terms: 'Para autorizar, aceite os riscos da conexão pelo computador e os Termos de Uso.',
    rate_limited: 'Muitas tentativas com códigos inválidos. Aguarde alguns minutos e tente novamente.',
    unknown: 'Não foi possível autorizar agora. Tente novamente.',
};

/** Lets the owner approve the computer that opened the panel from the OrganizaBot Conector. */
export default function ConnectorAuthorizationDialog(props: {
    userCode: string;
    business: BusinessDetails;
    isOwner: boolean;
    service: BusinessService;
    onApproved: () => void;
    onClose: () => void;
    // Logs out keeping the code, so the owner can sign in and come back to this dialog.
    onSwitchAccount: () => void;
}) {
    const { userCode, business, isOwner, service, onApproved, onClose, onSwitchAccount } = props;
    const [isApproving, setIsApproving] = useState(false);
    const [failure, setFailure] = useState<Failure | null>(null);
    const [isTermsRequired, setIsTermsRequired] = useState(!business.connectorTermsAccepted);
    const [isTermsChecked, setIsTermsChecked] = useState(false);

    const blocker: Blocker | null = !isOwner
        ? 'worker'
        : !hasPaidAccess(business) || failure === 'unavailable'
          ? 'unavailable'
          : business.whatsapp?.type === 'cloud_api' || failure === 'cloud_api'
            ? 'cloud_api'
            : null;
    const replacesPairedComputer = business.whatsappConnector != null || business.whatsapp?.type === 'connector';
    const canApprove = failure !== 'expired' && (!isTermsRequired || isTermsChecked);

    const handleApprove = async () => {
        gtagEvent('connector_authorization_approve_click');
        setIsApproving(true);
        setFailure(null);
        try {
            await service.approveWhatsAppConnector({ userCode, acceptTerms: isTermsRequired });
            onApproved();
        } catch (caught) {
            if (caught instanceof ConnectorCodeExpiredError) {
                setFailure('expired');
            } else if (caught instanceof WhatsAppAlreadyConnectedError) {
                setFailure('cloud_api');
            } else if (caught instanceof WhatsAppUnavailableError) {
                setFailure('unavailable');
            } else if (caught instanceof ConnectorTermsNotAcceptedError) {
                setIsTermsRequired(true);
                setFailure('terms');
            } else if (caught instanceof ConnectorApprovalRateLimitedError) {
                setFailure('rate_limited');
            } else {
                console.error('Failed to approve the WhatsApp connector', caught);
                setFailure('unknown');
            }
        } finally {
            setIsApproving(false);
        }
    };

    return (
        <Dialog isOpen onClose={onClose} allowCloseOnOverlay={false}>
            <DialogHeader>
                <DialogTitle>Conectar computador</DialogTitle>
            </DialogHeader>
            <DialogContent>
                <Body>
                    <span>
                        O OrganizaBot Conector quer conectar o WhatsApp
                        {business.name ? (
                            <>
                                {' '}
                                de <strong>{business.name}</strong>
                            </>
                        ) : null}{' '}
                        neste computador. Confira se o código abaixo é o mesmo que aparece no app:
                    </span>
                    <Code data-testid="connector-user-code">{userCode}</Code>
                    {blocker ? (
                        <ErrorText role="alert" data-testid="connector-authorization-blocked">
                            {blockerMessages[blocker]}
                        </ErrorText>
                    ) : (
                        <>
                            <Caution>Se não foi você que abriu o OrganizaBot Conector agora, cancele.</Caution>
                            {replacesPairedComputer && (
                                <ReplaceWarning data-testid="connector-replace-warning">
                                    Já existe um computador conectado. Ao autorizar este, o computador conectado hoje
                                    será desconectado.
                                </ReplaceWarning>
                            )}
                            {isTermsRequired && (
                                <ConnectorTermsCheckbox checked={isTermsChecked} onChange={setIsTermsChecked} />
                            )}
                            {failure && (
                                <ErrorText role="alert" data-testid="connector-authorization-error">
                                    {failureMessages[failure]}
                                </ErrorText>
                            )}
                        </>
                    )}
                </Body>
            </DialogContent>
            <DialogFooter>
                <Button onClick={onClose} disabled={isApproving}>
                    Cancelar
                </Button>
                {blocker === 'worker' && (
                    <Button primary onClick={onSwitchAccount} data-testid="connector-switch-account">
                        Sair e entrar como dono
                    </Button>
                )}
                {blocker === null && (
                    <Button
                        primary
                        onClick={handleApprove}
                        disabled={isApproving || !canApprove}
                        data-testid="connector-approve"
                    >
                        {isApproving ? 'Autorizando...' : 'Autorizar'}
                    </Button>
                )}
            </DialogFooter>
        </Dialog>
    );
}
